import React from 'react';
import { motion } from 'framer-motion';
import { Shield, CheckCircle, AlertCircle, ExternalLink, Wallet, Copy, Check, ArrowUpRight, Sparkles, Coffee, Moon, Zap, Users } from 'lucide-react';
import { SiDiscord, SiX, SiGithub } from '@icons-pack/react-simple-icons';
import PremiumCard from '../ui/PremiumCard';
import { formatNumber, formatDate } from '../../utils/formatters';
import { AMPOINTS_THRESHOLD } from '../../utils/constants';

const PassportTab = ({ 
  userData, 
  canMintPassport, 
  pointsNeeded,
  verifyAmPassport,
  verifyingPassport,
  passportError,
  linkX,
  linkGithub,
  linkDiscord,
  address,
  copiedField,
  setCopiedField
}) => {
  
  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(""), 2000);
  };

  // Calculate points breakdown from pointsStats/pointsBreakdown
  const pointsData = userData.pointsStats || userData.pointsBreakdown || {};
  
  // Build comprehensive points breakdown
  const pointsBreakdown = [
    { 
      category: 'AI Interactions', 
      points: userData.totalAiPoints || pointsData.ai || 0, 
      color: '#FFB800',
      icon: Sparkles
    },
    { 
      category: 'GitHub Activity', 
      points: pointsData.github || (userData.githubInteractionsCount * 10) || 0, 
      color: '#8B5CF6',
      icon: SiGithub
    },
    { 
      category: 'X Engagement', 
      points: pointsData.tweet || pointsData.x || (userData.xPostingsCount * 5) || 0, 
      color: '#1DA1F2',
      icon: SiX
    },
    { 
      category: 'GM Streaks', 
      points: pointsData.gm || 0, 
      color: '#F59E0B',
      icon: Coffee
    },
    { 
      category: 'GN Streaks', 
      points: pointsData.gn || 0, 
      color: '#6366F1',
      icon: Moon
    },
    { 
      category: 'SendIt Activity', 
      points: pointsData.sendit || 0, 
      color: '#10B981',
      icon: Zap
    },
    { 
      category: 'Role Assignments', 
      points: pointsData.role || 0, 
      color: '#EC4899',
      icon: Users
    },
    { 
      category: 'Staking Rewards', 
      points: pointsData.staking || 0, 
      color: '#8B5CF6',
      icon: Shield
    }
  ].filter(item => item.points > 0)
    .sort((a, b) => b.points - a.points); // Sort by points descending

  // Calculate percentages after filtering
  const totalPoints = userData.points || 1;
  const breakdownWithPercentage = pointsBreakdown.map(item => ({
    ...item,
    percentage: (item.points / totalPoints) * 100
  }));

  // Calculate "Other" if there's a difference
  const categorizedTotal = breakdownWithPercentage.reduce((sum, item) => sum + item.points, 0);
  const otherPoints = totalPoints - categorizedTotal;
  if (otherPoints > 0) {
    breakdownWithPercentage.push({
      category: 'Other Activities',
      points: otherPoints,
      color: '#6B7280',
      icon: Sparkles,
      percentage: (otherPoints / totalPoints) * 100
    });
  }

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-[#E8FDF2] mb-2">amPassport Status</h2>
        <p className="text-[#8AA5A2]">Manage your identity verification and connected accounts</p>
      </div>

      {/* Eligibility Card */}
      <PremiumCard className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-[#FFB800] to-[#FF8C00] rounded-xl flex items-center justify-center">
              <Shield className="w-6 h-6 text-[#0A0E1B]" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-[#E8FDF2]">Passport Eligibility</h3>
              <p className="text-sm text-[#8AA5A2]">
                {canMintPassport ? 'Ready to mint your passport' : `${formatNumber(pointsNeeded)} points needed`}
              </p>
            </div>
          </div>
          
          <div className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-full flex items-center gap-2 ${
            canMintPassport 
              ? 'bg-[#00FF88]/10 border border-[#00FF88]/30' 
              : 'bg-[#FFB800]/10 border border-[#FFB800]/30'
          }`}>
            {canMintPassport ? (
              <>
                <CheckCircle className="w-3.5 sm:w-4 h-3.5 sm:h-4 text-[#00FF88]" />
                <span className="text-xs sm:text-sm font-medium text-[#00FF88]">Eligible</span>
              </>
            ) : (
              <>
                <AlertCircle className="w-3.5 sm:w-4 h-3.5 sm:h-4 text-[#FFB800]" />
                <span className="text-xs sm:text-sm font-normal sm:font-medium text-[#FFB800]">Not Eligible</span>
              </>
            )}
          </div>
        </div>

        {/* Progress Section */}
        <div className="space-y-4">
          <div className="flex justify-between items-end">
            <div>
              <div className="text-3xl font-bold text-[#E8FDF2]">
                {formatNumber(userData.points)}
              </div>
              <div className="text-sm text-[#8AA5A2]">Total AmPoints</div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-semibold text-[#FFB800]">
                {formatNumber(AMPOINTS_THRESHOLD)}
              </div>
              <div className="text-sm text-[#8AA5A2]">Required</div>
            </div>
          </div>

          <div className="relative">
            <div className="h-3 bg-[#0A0E1B] rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-[#FFB800] to-[#FF8C00] rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${Math.min((userData.points / AMPOINTS_THRESHOLD) * 100, 100)}%` }}
                transition={{ duration: 1, ease: "easeOut" }}
              />
            </div>
            <div className="absolute -top-1 -right-1 text-xs text-[#8AA5A2]">
              {Math.min((userData.points / AMPOINTS_THRESHOLD) * 100, 100).toFixed(0)}%
            </div>
          </div>
        </div>

        {/* Points Breakdown */}
        {breakdownWithPercentage.length > 0 && (
          <div className="mt-6 pt-6 border-t border-white/[0.08]">
            <h4 className="text-sm font-medium text-[#8AA5A2] mb-4">Points Breakdown</h4>
            <div className="space-y-3">
              {breakdownWithPercentage.map((item, index) => {
                const IconComponent = item.icon;
                return (
                  <div key={index} className="flex items-center justify-between group">
                    <div className="flex items-center gap-3 flex-1">
                      <div 
                        className="w-8 h-8 rounded-lg flex items-center justify-center transition-all group-hover:scale-110"
                        style={{ backgroundColor: `${item.color}20` }}
                      >
                        <IconComponent 
                          className="w-4 h-4" 
                          style={{ color: item.color }}
                        />
                      </div>
                      <span className="text-sm text-[#E8FDF2]">{item.category}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-24 h-1.5 bg-[#0A0E1B] rounded-full overflow-hidden">
                        <motion.div 
                          className="h-full rounded-full transition-all duration-500"
                          initial={{ width: 0 }}
                          animate={{ width: `${item.percentage}%` }}
                          transition={{ duration: 1, delay: index * 0.1 }}
                          style={{ backgroundColor: item.color }}
                        />
                      </div>
                      <span className="text-sm text-[#8AA5A2] w-16 text-right">
                        {formatNumber(item.points)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Tips Summary */}
            <div className="mt-4 pt-4 border-t border-white/[0.05] grid grid-cols-2 gap-4">
              <div className="text-center p-3 bg-[#0A0E1B]/40 rounded-xl">
                <div className="text-xs text-[#8AA5A2] mb-1">Tips Sent</div>
                <div className="text-lg font-semibold text-[#E8FDF2]">
                  {userData.totalTipsSent || '0'} SEND
                </div>
              </div>
              <div className="text-center p-3 bg-[#0A0E1B]/40 rounded-xl">
                <div className="text-xs text-[#8AA5A2] mb-1">Tips Received</div>
                <div className="text-lg font-semibold text-[#00FF88]">
                  {userData.totalTipsReceived || '0'} SEND
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3 mt-6">
          <button
            onClick={verifyAmPassport}
            disabled={verifyingPassport}
            className={`flex-1 px-4 py-3 rounded-xl font-medium transition-all flex items-center justify-center gap-2 ${
              canMintPassport
                ? 'bg-gradient-to-r from-[#FFB800] to-[#FF8C00] text-[#0A0E1B] hover:opacity-90'
                : 'bg-[#1A1F2E] text-[#8AA5A2] cursor-not-allowed'
            }`}
          >
            <Shield className="w-5 h-5" />
            {verifyingPassport ? "Verifying..." : "Verify Passport"}
          </button>
          
          <button
            onClick={() => window.open(`https://ao.link/entity/${userData.processId}`, '_blank')}
            className="px-4 py-3 rounded-xl font-medium bg-[#1A1F2E] text-[#E8FDF2] hover:bg-[#242938] transition-all flex items-center gap-2"
          >
            <ArrowUpRight className="w-5 h-5" />
            <span className="hidden sm:inline">View Onchain</span>
          </button>
        </div>

        {passportError && (
          <motion.div 
            className={`mt-4 p-4 rounded-xl flex items-start gap-3 ${
              passportError.includes("verified") || passportError.includes("Balance") 
                ? 'bg-green-500/10 border border-green-500/20' 
                : 'bg-red-500/10 border border-red-500/20'
            }`}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <AlertCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
              passportError.includes("verified") || passportError.includes("Balance")
                ? 'text-green-400'
                : 'text-red-400'
            }`} />
            <span className={`text-sm ${
              passportError.includes("verified") || passportError.includes("Balance")
                ? 'text-green-400'
                : 'text-red-400'
            }`}>{passportError}</span>
          </motion.div>
        )}
      </PremiumCard>

      {/* Connected Accounts */}
      <PremiumCard className="p-6">
        <h3 className="text-xl font-semibold text-[#E8FDF2] mb-6">Connected Accounts</h3>
        
        <div className="space-y-3">
          {[
            { 
              platform: "Discord", 
              icon: SiDiscord, 
              value: userData.userId,
              linked: true,
              color: "#5865F2"
            },
            { 
              platform: "X (Twitter)", 
              icon: SiX, 
              value: userData.xId,
              linked: userData.xId !== "Not linked",
              action: linkX,
              link: userData.xId !== "Not linked" ? `https://x.com/${userData.xId}` : null,
              color: "#000000"
            },
            { 
              platform: "GitHub", 
              icon: SiGithub, 
              value: userData.githubId,
              linked: userData.githubId !== "Not linked",
              action: linkGithub,
              link: userData.githubId !== "Not linked" ? `https://github.com/${userData.githubId}` : null,
              color: "#333333"
            }
          ].map((account) => {
            const IconComponent = account.icon;
            return (
              <div 
                key={account.platform} 
                className="p-4 bg-[#1A1F2E] rounded-xl flex items-center justify-between hover:bg-[#242938] transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    account.linked ? 'bg-white/10' : 'bg-white/5'
                  }`}>
                    <IconComponent 
                      className="w-5 h-5" 
                      style={{ color: account.linked ? '#E8FDF2' : '#5A6B68' }}
                    />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-[#E8FDF2]">{account.platform}</div>
                    <div className="text-xs text-[#8AA5A2]">
                      {account.linked ? account.value : "Not connected"}
                    </div>
                  </div>
                </div>
                
                {!account.linked && account.action ? (
                  <button
                    onClick={account.action}
                    className="px-3 py-1.5 text-sm font-medium text-[#8AA5A2] hover:text-[#E8FDF2] transition-colors"
                  >
                    Connect
                  </button>
                ) : account.linked && account.link ? (
                  <a 
                    href={account.link} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="p-2 hover:bg-white/5 rounded-lg transition-colors"
                  >
                    <ExternalLink className="w-4 h-4 text-[#8AA5A2]" />
                  </a>
                ) : account.linked ? (
                  <CheckCircle className="w-4 h-4 text-[#00FF88]" />
                ) : null}
              </div>
            );
          })}
        </div>
      </PremiumCard>

      {/* Connected Wallets */}
      <PremiumCard className="p-6">
        <h3 className="text-xl font-semibold text-[#E8FDF2] mb-6">Connected Wallets</h3>
        
        <div className="space-y-3">
          {/* Current Active Wallet */}
          <div className="p-4 bg-[#00FF88]/5 border border-[#00FF88]/20 rounded-xl flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#00FF88]/20 flex items-center justify-center">
                <Wallet className="w-5 h-5 text-[#00FF88]" />
              </div>
              <div>
                <div className="text-sm font-medium text-[#E8FDF2]">Active Wallet</div>
                <div className="text-xs text-[#8AA5A2] font-mono">
                  {address.substring(0, 6)}...{address.substring(address.length - 6)}
                </div>
              </div>
            </div>
            
            <button
              onClick={() => copyToClipboard(address, 'active-wallet')}
              className="p-2 hover:bg-white/5 rounded-lg transition-colors"
            >
              {copiedField === 'active-wallet' ? (
                <Check className="w-4 h-4 text-[#00FF88]" />
              ) : (
                <Copy className="w-4 h-4 text-[#8AA5A2]" />
              )}
            </button>
          </div>

          {/* Other Wallets */}
          {userData.walletAddresses && userData.walletAddresses
            .filter(w => {
              const walletAddr = typeof w === 'string' ? w : w.walletAddress;
              return walletAddr && walletAddr.length > 12 && walletAddr !== address;
            })
            .map((w, i) => {
              const walletStr = typeof w === 'string' ? w : w.walletAddress;
              const antName = typeof w === 'object' ? w.antName : null;
              const timestamp = typeof w === 'object' ? w.timestamp : null;
              
              return (
                <div 
                  key={walletStr}
                  className="p-4 bg-[#1A1F2E] rounded-xl flex items-center justify-between hover:bg-[#242938] transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center">
                      <Wallet className="w-5 h-5 text-[#8AA5A2]" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-[#E8FDF2] flex items-center gap-2">
                        <span className="font-mono">
                          {walletStr.substring(0, 6)}...{walletStr.substring(walletStr.length - 6)}
                        </span>
                        {antName && (
                          <span className="text-xs px-2 py-0.5 bg-[#FFB800]/10 text-[#FFB800] rounded">
                            {antName}
                          </span>
                        )}
                      </div>
                      {timestamp && (
                        <div className="text-xs text-[#5A6B68]">
                          Added {formatDate(timestamp)}
                        </div>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => copyToClipboard(walletStr, `wallet-${i}`)}
                    className="p-2 hover:bg-white/5 rounded-lg transition-colors"
                  >
                    {copiedField === `wallet-${i}` ? (
                      <Check className="w-4 h-4 text-[#00FF88]" />
                    ) : (
                      <Copy className="w-4 h-4 text-[#8AA5A2]" />
                    )}
                  </button>
                </div>
              );
            })}
            
          {(!userData.walletAddresses || userData.walletAddresses.filter(w => {
            const walletAddr = typeof w === 'string' ? w : w.walletAddress;
            return walletAddr && walletAddr.length > 12 && walletAddr !== address;
          }).length === 0) && (
            <div className="p-8 text-center text-[#5A6B68] text-sm">
              No additional wallets connected
            </div>
          )}
        </div>
      </PremiumCard>
    </div>
  );
};

export default PassportTab;